/**
 * Utility functions for license selection logic
 */

// Count the number of true values in license meta object
export const countMetaTrueValues = (meta) => {
  if (!meta) return 0;
  return Object.values(meta).filter((value) => value === true).length;
};

// Filter out blocked and expired licenses
export const filterValidLicenses = (licenses) => {
  if (!licenses) return [];
  return licenses.filter(
    (license) => !license.is_blocked && new Date(license.expires) >= new Date()
  );
};

// Select the license with the most true values in meta
export const selectBestLicense = (validLicenses) => {
  if (!validLicenses || validLicenses.length === 0) return null;

  return [...validLicenses].sort((a, b) => {
    return countMetaTrueValues(b.meta) - countMetaTrueValues(a.meta);
  })[0];
};

// Create a master license that combines all valid licenses
// - expires: earliest expiration date among all valid licenses
// - meta: combined credentials (true if any license has it true)
// - type: from the license with the most credentials (best license)
export const createMasterLicense = (validLicenses) => {
  if (!validLicenses || validLicenses.length === 0) return null;

  // Find the earliest expiration date
  const earliestExpires = validLicenses.reduce((earliest, license) => {
    const licenseDate = new Date(license.expires);
    return licenseDate < earliest ? licenseDate : earliest;
  }, new Date(validLicenses[0].expires));

  // Combine all meta credentials (true if any license has it true)
  // If a license has no meta, treat it as { maastotaulukot: true }
  const combinedMeta = validLicenses.reduce((combined, license) => {
    const meta = license.meta || { maastotaulukot: true };
    Object.keys(meta).forEach((key) => {
      if (meta[key] === true) {
        combined[key] = true;
      } else if (!(key in combined)) {
        combined[key] = false;
      }
    });
    return combined;
  }, {});

  // Get the best license for type selection
  const bestLicense = selectBestLicense(validLicenses);

  return {
    key: 'MASTER',
    expires: earliestExpires.toISOString(),
    is_blocked: false,
    meta: combinedMeta,
    type: bestLicense.type,
  };
};
